import {
  NetworkingType,
  ParticipantInteractionEffect,
  ParticipantInteractionType,
} from "@prisma/client";

export type CapabilityRuleInput = {
  interactionType: ParticipantInteractionType;
  effect: ParticipantInteractionEffect;
  targetGroupId: string | null;
  networkingPrivacy?: NetworkingType | null;
};

export type CapabilityBuckets = {
  canConnectWith: string[];
  canChatWith: string[];
  canBookMeetingWith: string[];
  canMatchmakeWith: string[];
  canAccessFloorPlan: boolean;
  canAccessBookmarks: boolean;
  canAccessAgenda: boolean;
  privacySettings: NetworkingType | null;
  canAccessPersonalDetails: boolean;
  canAccessMyBadge: boolean;
  canAccessMyAgenda: boolean;
  canAccessMyMeetings: boolean;
  canAccessMyConnections: boolean;
  canAccessAnalytics: boolean;
  canAccessNotifications: boolean;
  canAccessTeam: boolean;
  canAccessProducts: boolean;
  canAccessDocuments: boolean;
  canAccessVideos: boolean;
  canAccessCalendar: boolean;
  canAccessLeads: boolean;
};

/** Fold interaction-rule scalars into capability buckets. Uses targetGroupId, not a joined targetGroup. */
export function applyCapabilityRules(
  result: CapabilityBuckets,
  rules: CapabilityRuleInput[]
) {
  for (const rule of rules) {
    const { interactionType, effect, targetGroupId } = rule;
    const isAllowed = effect === ParticipantInteractionEffect.ALLOW;

    switch (interactionType) {
      case ParticipantInteractionType.CONNECT:
        if (isAllowed && targetGroupId)
          result.canConnectWith.push(targetGroupId);
        break;
      case ParticipantInteractionType.CHAT:
        if (isAllowed && targetGroupId) result.canChatWith.push(targetGroupId);
        break;
      case ParticipantInteractionType.BOOKMEETING:
        if (isAllowed && targetGroupId)
          result.canBookMeetingWith.push(targetGroupId);
        break;
      case ParticipantInteractionType.PERSONAL_DETAILS:
        result.canAccessPersonalDetails = isAllowed;
        break;
      case ParticipantInteractionType.MY_BADGE:
        result.canAccessMyBadge = isAllowed;
        break;
      case ParticipantInteractionType.MY_AGENDA:
        result.canAccessMyAgenda = isAllowed;
        break;
      case ParticipantInteractionType.MY_MEETINGS:
        result.canAccessMyMeetings = isAllowed;
        break;
      case ParticipantInteractionType.MY_CONNECTIONS:
        result.canAccessMyConnections = isAllowed;
        break;
      case ParticipantInteractionType.ANALYTICS:
        result.canAccessAnalytics = isAllowed;
        break;
      case ParticipantInteractionType.NOTIFICATIONS:
        result.canAccessNotifications = isAllowed;
        break;
      case ParticipantInteractionType.TEAM:
        result.canAccessTeam = isAllowed;
        break;
      case ParticipantInteractionType.PRODUCTS:
        result.canAccessProducts = isAllowed;
        break;
      case ParticipantInteractionType.VIDEOS:
        result.canAccessVideos = isAllowed;
        break;
      case ParticipantInteractionType.DOCUMENTS:
        result.canAccessDocuments = isAllowed;
        break;
      case ParticipantInteractionType.CALENDAR:
        result.canAccessCalendar = isAllowed;
        break;
      case ParticipantInteractionType.LEADS:
        result.canAccessLeads = isAllowed;
        break;
      case ParticipantInteractionType.MATCHMAKING:
        if (isAllowed && targetGroupId)
          result.canMatchmakeWith.push(targetGroupId);
        break;
      case ParticipantInteractionType.FLOOR_PLAN:
        result.canAccessFloorPlan = isAllowed;
        break;
      case ParticipantInteractionType.BOOKMARKS:
        result.canAccessBookmarks = isAllowed;
        break;
      case ParticipantInteractionType.AGENDA:
        result.canAccessAgenda = isAllowed;
        break;
      case ParticipantInteractionType.NETWORKING_PRIVACY:
        result.privacySettings = isAllowed
          ? (rule.networkingPrivacy ?? null)
          : null;
        break;
      default:
        break;
    }
  }
}
